import { navigate } from '@reach/router';
import { AppPath } from 'appConstants';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { accessInformation, ISubscription, SubscriptionsList } from './models';
import { Spinner } from 'components/spinner/Spinner';
import { SubscriptionsApi } from 'api/subscriptionsApi';
import { RenewSubscriptionsForm } from './RenewSubscriptionsForm';
import { RenewSubscriptionsViaInvoiceForm } from './RenewSubscriptionsViaInvoiceForm';
import ToggleSwitch from 'components/shared/ToggleSwitch';
import { CollapsePanel } from 'components/collapsePanel/CollapsePanel';
import { isBeforeExpiryDate, isTodayExpiryDate } from 'subscriptionOptions/renewalDates';
import { createOptionsCache } from 'hooks/useFetchOptions';
import { ManageSubRenewal, RenewalPaymentRequest, SelectionChange } from '../subscriptions/ManageSubRenewal';
import {
  finishLeaving,
  groupForSubscription,
  groupRenewalSubjects,
  isLapsed,
  panelsAfterCard,
  reconcileRenderedGroups,
  RenderedGroup,
} from 'subscriptionOptions/renewalGroups';
import { applySelection, selectionToPackageIds } from 'subscriptionOptions/optionsGrid';
import { renewalCardPrice, renewalPricesBySubject } from 'subscriptionOptions/renewalPricing';
import {
  discountCandidates,
  discountStatus,
  keepPricedGroups,
  MissedDiscountReasons,
  PricedPackagesByGroup,
} from 'subscriptionOptions/multiSubjectDiscount';
import { RenewalDiscountLostModal } from './RenewalDiscountLostModal';
import { GridSelection } from 'subscriptionOptions/types';
import {
  applyPromoCodeWipes,
  codesForSubjects,
  droppedCodes,
  promoCodeBySubject,
  readPromoCodeWipes,
  PanelRejection,
  readRenewalPromoCodes,
  rejectionForPanel,
  RenewalPromoCode,
  wipedSubjectIds,
} from 'subscriptionOptions/renewalPromoCodes';

type Props = {
  schoolName?: string;
  schoolURN?: string | null;
};

export const Subscriptions: React.FC<Props> = ({ schoolName, schoolURN }) => {
  const [isRenewMenuOpen, setIsRenewMenuOpen] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);

  const [subsToPay, setSubsToPay] = useState<number[] | null>(null);
  const [pkgsToPay, setPkgsToPay] = useState<number[] | null>(null);
  /** The paying panel's picks, so the payment prices what the panel quoted. */
  const [selectionToPay, setSelectionToPay] = useState<GridSelection>({});
  /** The paying panel's subjects, so its codes stay live while the payment screen is open. */
  const [subjectsToPay, setSubjectsToPay] = useState<number[]>([]);
  const [sideMenuTitle, setSideMenuTitle] = useState('');
  const [updateList, setUpdateList] = useState(true);
  const [accessInfo, setAccessInfo] = useState<accessInformation | null>(null);
  const [subscriptionsListInfo, setSubscriptionsListInfo] =
    useState<SubscriptionsList | null>(null);
  const [renewalPromoCodeLoaded, setRenewalPromoCodeLoaded] = useState<boolean>(false);
  /**
   * The account's codes, as getrenewalpromocode last reported them. Every write replaces this from
   * its response; a package change subtracts the wipes that response carries.
   */
  const [renewalPromoCodes, setRenewalPromoCodes] = useState<RenewalPromoCode[]>([]);
  const [promoCodeError, setPromoCodeError] = useState<string>('');
  /** Codes a write removed without being asked to, so a panel can explain the missing row. */
  const [promoCodesDropped, setPromoCodesDropped] = useState<string[]>([]);
  /** Subjects a package change wiped a code from; the affected panel says so inline. */
  const [promoCodesWipedSubjects, setPromoCodesWipedSubjects] = useState<number[]>([]);
  /** A rejected code. Unlike a failed read, it never blocks pricing. */
  const [promoCodeRejection, setPromoCodeRejection] = useState<PanelRejection | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  /* ---- renewal panels: one per auto-renew group, regrouped from the confirmed list ---- */

  const renewalGroups = useMemo(
    () => (subscriptionsListInfo ? groupRenewalSubjects(subscriptionsListInfo) : []),
    [subscriptionsListInfo]
  );
  const [renderedGroups, setRenderedGroups] = useState<RenderedGroup[]>([]);
  /** Held above the panels so a merge or split keeps what the school chose. */
  const [renewalSelection, setRenewalSelection] = useState<GridSelection>({});
  const optionsCache = useRef(createOptionsCache());
  /**
   * What each panel's summary last priced, for the card prices and the discount check. Kept per
   * group key, so a collapsing panel cannot overwrite the prices of the panel replacing it.
   */
  const [pricedPackages, setPricedPackages] = useState<PricedPackagesByGroup>({});
  /** Why a change just lost the multi-subject discount; shown until dismissed. */
  const [lostDiscount, setLostDiscount] = useState<MissedDiscountReasons | null>(null);
  /** Whether the discount applied when every panel last had a price; null until they have. */
  const discountWasApplied = useRef<boolean | null>(null);

  useEffect(() => {
    setRenderedGroups((prev) => reconcileRenderedGroups(prev, renewalGroups));
  }, [renewalGroups]);

  useEffect(() => {
    setPricedPackages((prev) => keepPricedGroups(prev, renderedGroups.map((r) => r.group.key)));
  }, [renderedGroups]);

  // Compared between settled prices only, so a panel still pricing is never read as a loss.
  useEffect(() => {
    const status = discountStatus(discountCandidates(renewalGroups, pricedPackages));
    if (!status) return;

    if (discountWasApplied.current && status.lostTo) {
      setLostDiscount(status.lostTo);
    }
    discountWasApplied.current = status.applied;
  }, [renewalGroups, pricedPackages]);

  const handlePanelLeft = (key: string) => {
    setRenderedGroups((prev) => finishLeaving(prev, key));
  };

  const handleSelectionChange = (changes: SelectionChange[]) => {
    setRenewalSelection((prev) => changes.reduce((next, c) => applySelection(next, c.subjectsId, c.packagesId), prev));
    // The notices explain the previous write; a new choice supersedes them.
    setPromoCodesDropped([]);
    setPromoCodesWipedSubjects([]);
  };

  /** The payment modal's starting codes; it copies them on open and never writes them back. */
  const codesToPay = useMemo(
    () => codesForSubjects(renewalPromoCodes, subjectsToPay),
    [renewalPromoCodes, subjectsToPay]
  );
  const promoCodesToPay = useMemo(() => promoCodeBySubject(codesToPay), [codesToPay]);

  const handleOpenPayment = (request: RenewalPaymentRequest) => {
    setSubsToPay(request.subjectIds);
    setPkgsToPay(selectionToPackageIds(request.selection));
    setSelectionToPay(request.selection);
    setSubjectsToPay(request.subjectIds);
    setPaymentMethod(request.method);
    setSideMenuTitle(request.method === 'card' ? 'Pay By Card' : 'Pay By Invoice');
    setIsRenewMenuOpen(true);
  };

  /**
   * Counts requests that replace the code set, so a slow one landing late is discarded. A package
   * change stays outside this: its wipes subtract from whatever the set holds when they arrive.
   */
  const promoCodeReadSequence = useRef<number>(0);

  /**
   * The single source of truth for what pricing is sent. Recalled on load, after a rejected write,
   * after an auto-renew toggle and after a payment. A package change does not recall it: it
   * reports its own wipes.
   */
  const loadRenewalPromoCodes = async () => {
    const sequence = ++promoCodeReadSequence.current;
    const result = await SubscriptionsApi.getRenewalPromoCode();
    if (sequence !== promoCodeReadSequence.current) return;

    if (result.data && result.data.status === '1') {
      setRenewalPromoCodes(readRenewalPromoCodes(result.data));
      setPromoCodeError('');
    } else {
      // Pricing must not run on codes we cannot vouch for.
      setPromoCodeError('Your promo codes could not be loaded, so prices are unavailable. Please refresh the page.');
    }

    setRenewalPromoCodeLoaded(true);
  };

  /**
   * Applies or removes one code. The response reports the resulting codes either way, so they are
   * rendered either way; a rejection goes to the promo box rather than blocking prices.
   */
  const writeRenewalPromoCode = async (
    code: string,
    action: 'set' | 'unset',
    groupKey: string
  ): Promise<string> => {
    const before = renewalPromoCodes;
    const sequence = ++promoCodeReadSequence.current;
    setPromoCodeRejection(null);

    const result = await SubscriptionsApi.setRenewalPromoCode(code, action);
    if (sequence !== promoCodeReadSequence.current) return '';

    if (result.data && result.data.details) {
      const after = readRenewalPromoCodes(result.data);
      setRenewalPromoCodes(after);
      setPromoCodesDropped(action === 'set' ? droppedCodes(before, after) : []);
      setPromoCodesWipedSubjects([]);
    }

    const rejection = result.error ? result.error.message || '' : '';
    setPromoCodeRejection(rejection ? { groupKey, message: rejection } : null);

    // A rejection may answer with an error envelope rather than the code list.
    if (result.error || !(result.data && result.data.details)) {
      loadRenewalPromoCodes();
    }

    return rejection;
  };

  /** Subtracts the codes a package change wiped, per package, without a recall. */
  const applyRenewalSaveResult = (responseData: any) => {
    const wipes = readPromoCodeWipes(responseData);

    setRenewalPromoCodes((prev) => applyPromoCodeWipes(prev, wipes));
    setPromoCodesWipedSubjects(wipedSubjectIds(wipes));
    setPromoCodesDropped([]);
    fetchSubscriptionsList(true);
  };


  /** `silent` skips the page spinner, so panels stay mounted. */
  const fetchSubscriptionsList = async (silent: boolean = false) => {
    const result = await SubscriptionsApi.getSubscriptionsList();

    if (!silent) {
      setLoading(true);
    }
    setError('');

    if (!result) {
      setError('An error occurred while trying to load subscriptions.');
      setLoading(false);
      return;
    }

    if (result.error) {
      setError(
        result.error.message ||
          'An error occurred while trying to load subscriptions.'
      );
      setLoading(false);
      return;
    }

    if (result.data) {
      const { listPackages, bCanAddFreeTrial, bCanAddSubscription } =
        result.data;

      if(listPackages){
        listPackages.map((item: any) => {
          item.isLoading = false;
          return item;
        });
      }

      setSubscriptionsListInfo(listPackages ? listPackages.filter((item: ISubscription) => !isLapsed(item)) : []);
      setAccessInfo({ bCanAddFreeTrial, bCanAddSubscription });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!updateList) return;
    fetchSubscriptionsList();
    loadRenewalPromoCodes();
    setUpdateList(false);
  }, [updateList]);

  useEffect(() => {
    if(!isRenewMenuOpen){
      document.body.style.overflowY = '';
    }
  }, [isRenewMenuOpen]);

  const handleNewSubscription = () => {
    navigate(AppPath.NEW_SUBSCRIPTION);
  };

  const handleNewFreeTrial = () => {
    navigate(AppPath.NEW_FREE_TRIAL);
  };

  /**
   * Updates the list only after the server confirms, so panels never regroup on a failed toggle.
   * Regrouping re-splits options and pricing but writes nothing. Patched by subscription id, not
   * list position, which a concurrent refetch can invalidate.
   */
  const updateAutoRenewSub = async (schoolAccountPackagesId: number, bAutoRenew: boolean) => {
    setError('');

    const patch = (changes: Partial<ISubscription>) =>
      setSubscriptionsListInfo((prev) =>
        prev
          ? prev.map((item) =>
              item.schoolAccountPackagesId === schoolAccountPackagesId ? { ...item, ...changes } : item
            )
          : prev
      );

    patch({ isLoading: true });

    const result = await SubscriptionsApi.autoRenewSubscription(schoolAccountPackagesId, !bAutoRenew);

    if (result.data && result.data.status === "1") {
      patch({ isLoading: false });
      // Server truth for bAutoRenew, plus the codes as they now stand, before panels regroup.
      await Promise.all([fetchSubscriptionsList(true), loadRenewalPromoCodes()]);
    } else {
      patch({ isLoading: false });
      setError('An error occurred while updating auto-renew for subscription.');
    }
  };

  const renderRenewalPanels = (afterIndex: number, cardCount: number) => {
    if (!renewalPromoCodeLoaded) return null;

    return panelsAfterCard(renderedGroups, afterIndex, cardCount).map((rendered) => (
      <CollapsePanel
        key={'Renewal_Panel_' + rendered.group.key}
        phase={rendered.phase}
        className="SRPanelWrap"
        onLeft={() => handlePanelLeft(rendered.group.key)}
      >
        <ManageSubRenewal
          group={rendered.group}
          selection={renewalSelection}
          onSelectionChange={handleSelectionChange}
          optionsCache={optionsCache.current}
          promoCodes={renewalPromoCodes}
          promoCodeError={promoCodeError}
          promoCodeRejection={rejectionForPanel(promoCodeRejection, rendered.group.key)}
          droppedPromoCodes={promoCodesDropped}
          wipedSubjectIds={promoCodesWipedSubjects}
          onApplyPromoCode={(code: string) => writeRenewalPromoCode(code, 'set', rendered.group.key)}
          onRemovePromoCode={(code: string) => writeRenewalPromoCode(code, 'unset', rendered.group.key)}
          onRenewalSaved={applyRenewalSaveResult}
          onOpenPayment={handleOpenPayment}
          onPackagesPriced={(packages) => setPricedPackages((prev) => ({ ...prev, [rendered.group.key]: packages }))}
        />
      </CollapsePanel>
    ));
  };

  return (
    <>
      <div className="PageContent SubscriptionsPage">
        <div className="InfoTableHdr">
          <div className="LeftSide">
            {(schoolName || schoolURN) &&
              <h1>
                {schoolName &&
                  <>{schoolName}</>
                }
                {schoolName && schoolURN && <>, </>}
                {schoolURN &&
                  <>URN: {schoolURN}</>
                }
              </h1>
            }
          </div>
          <div className="RightSide">
            {subscriptionsListInfo &&
              accessInfo?.bCanAddFreeTrial && (
                <div className="Btn SecondBtn" onClick={handleNewFreeTrial}>Add a free trial</div>
              )}
            {subscriptionsListInfo &&
              accessInfo?.bCanAddSubscription && (
                <div className="Btn" onClick={handleNewSubscription}>Add a new subscription</div>
              )}
          </div>
        </div>

        {loading ?
          <Spinner />
          :
          <>
            {error ?
              <div className="ErrorTxt3">{error}</div>
              :
              <>
                {subscriptionsListInfo && subscriptionsListInfo.length > 0 ?
                  <div className="ManageSchoolSubContainer">
                    {subscriptionsListInfo.map((sub: ISubscription, index: number) => {
                      // The list's price is the fallback: it prices each subject alone, the panel as a basket.
                      const panelGroup = groupForSubscription(renewalGroups, sub);
                      const panelPrice =
                        panelGroup && renewalPricesBySubject(pricedPackages[panelGroup.key] || [])[sub.subjectsId];
                      const cardPrice = panelPrice
                        ? renewalCardPrice(panelPrice.netUndiscounted, panelPrice.netDiscounted)
                        : renewalCardPrice(sub.renewalPackageNetUndiscountedPrice, sub.renewalPackageNetDiscountedPrice);
                      const subEndingSoon = sub.bRenewable && sub.renewalDate && (isTodayExpiryDate(sub.renewalDate) || !isBeforeExpiryDate(sub.renewalDate));
                      // Past the renewal date but still renewable.
                      const subEnded = sub.bRenewable && sub.renewalDate && isBeforeExpiryDate(sub.renewalDate) && !isTodayExpiryDate(sub.renewalDate);

                      return (
                        <React.Fragment key={sub.subjectName + sub.currentPackageFullName}>
                        <div
                          className={
                            "ManageSchoolSubDetails" +
                            (sub.isLoading ? " LoadingPanel" : "") +
                            ((subEndingSoon || subEnded) ? " SubEndingSoon" : "")
                          }
                        >
                          {sub.isLoading &&
                            <Spinner />
                          }
                          <div className="MSSubTopRow">
                            <div className="MSTRCol LeftCol">
                              <div className="TitleRow">
                                <div className="TRLeftCol">
                                  <h2>{sub.subjectName}</h2>
                                </div>
                                {subEnded ?
                                  <div className="TRRightCol">
                                    <div className="SubEndingSoonTxt SubEnded">Already ended</div>
                                  </div>
                                  :
                                subEndingSoon ?
                                  <div className="TRRightCol">
                                    <div className="SubEndingSoonTxt">Subscription ending soon</div>
                                  </div>
                                  :
                                  <>
                                    {!isBeforeExpiryDate(sub.renewalDate) &&
                                      <div className="TRRightCol">
                                        <div className="SubEndingSoonTxt ActiveSub">Active subscription</div>
                                      </div>
                                    }
                                  </>
                                }
                              </div>

                              {!sub.bMarkSubscriptionAsPaid && <h4>Subscription awaiting approval</h4>}
                            </div>
                            <div className="MSTRCol RightCol">
                              <div
                                className={"Btn AutoRenewBtn" + (sub.bAutoRenew ? " BtnWhiteOrange" : " BtnWhiteGrey")}
                                onClick={() => {
                                  if (sub.isLoading) return;
                                  updateAutoRenewSub(sub.schoolAccountPackagesId, sub.bAutoRenew);
                                }}
                              >
                                <span className="ARInner">
                                  <ToggleSwitch
                                    onChange={(e) => {}}
                                    value={sub.bAutoRenew}
                                  />
                                  <span className="ARTxt">Auto-renew subscription</span>
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="MSSubMidRow">
                            <p className="MSCurrentSubTxt">Current subscription: <strong>{sub.currentPackageFullName} <span className="BreakIcon">.</span> {sub.fromDate} to {sub.toDate}</strong></p>
                          </div>
                          <div className="MSSubBtmRow">
                            <div className="MSSBCol">
                              <p>Renewal date</p>
                              <h3>{sub.renewalDate}</h3>
                            </div>
                            {sub.renewalPackageFullName &&
                              <div className="MSSBCol">
                                <p>Renewal package</p>
                                <h3>{sub.renewalPackageFullName}</h3>
                              </div>
                            }
                            <div className="MSSBCol">
                              <p>Renewal price</p>
                              {cardPrice &&
                                <h3>
                                  £{cardPrice.price.toFixed(2)}
                                  {cardPrice.was !== null &&
                                    <>&nbsp;&nbsp;<s>{"£" + cardPrice.was.toFixed(2)}</s></>
                                  }
                                </h3>
                              }
                            </div>
                          </div>
                        </div>

                        {/* A panel sits after the last card of its auto-renew group. */}
                        {renderRenewalPanels(index, subscriptionsListInfo.length)}
                        </React.Fragment>
                      );
                    })}
                  </div>
                  :
                  <h3 className="GeneralMsg">You have no subscriptions.</h3>
                }
              </>
            }
          </>
        }

        {lostDiscount && (
          <RenewalDiscountLostModal reasons={lostDiscount} onClose={() => setLostDiscount(null)} />
        )}

        {/* Renew subscription via card */}
        {isRenewMenuOpen && subsToPay && pkgsToPay && paymentMethod === "card" && (
          <RenewSubscriptionsForm
            panelOpen={isRenewMenuOpen}
            togglePanelOpen={setIsRenewMenuOpen}
            menuTitle={sideMenuTitle}
            id={subsToPay}
            setUpdateList={setUpdateList}
            setSubsToPay={setSubsToPay}
            setPkgsToPay={setPkgsToPay}
            packageIds={pkgsToPay}
            setPaymentMethod={setPaymentMethod}
            selection={selectionToPay}
            promoCodeBySubject={promoCodesToPay}
            promoCodes={codesToPay}
          />
        )}
        {/* Renew subscription via invoice */}
        {isRenewMenuOpen && subsToPay && pkgsToPay && paymentMethod === "invoice" && (
          <RenewSubscriptionsViaInvoiceForm
            panelOpen={isRenewMenuOpen}
            togglePanelOpen={setIsRenewMenuOpen}
            menuTitle={sideMenuTitle}
            id={subsToPay}
            setUpdateList={setUpdateList}
            setSubsToPay={setSubsToPay}
            setPkgsToPay={setPkgsToPay}
            packageIds={pkgsToPay}
            setPaymentMethod={setPaymentMethod}
            selection={selectionToPay}
            promoCodeBySubject={promoCodesToPay}
            promoCodes={codesToPay}
          />
        )}
      </div>
    </>
  );
};
